/**
 * basis (#2304) — the generic "Bezugsgröße" (reference quantity) behind the DB1
 * ranking. DB1 is a fixed euro amount per crop_cycle; the *denominator* it is
 * divided by is a UI dimension, never a stored identity (finance ontology
 * principle 10): € gesamt (the euro itself, divided by nothing — joined the page on
 * 2026-09-07, it had been drawer-only), je Hektar (``area_ha``), je kg (the harvest quantity, every yield
 * unit this app can name normalised to kg via ``KG_PER_YIELD_UNIT``, #2930), je
 * Reihenmeter (``row_length_m``) and je Pflanze (``plant_count``) — the last two
 * derived by #2303's geometry materialiser and present on the fact ONLY when
 * derivable (key-absence == basis unavailable). All math is null-honest: an
 * unknown or zero denominator yields null (sort last, render em dash), never a
 * fabricated 0 €/X or a divide-by-zero.
 */
import type {
  DeckungsbeitragRow,
  GeometryProvenance,
  GeometryProvenanceEntry,
  KulturRollupRow,
} from './types';
import { kgPerYieldUnit } from './yieldUnit';

/** The five reference quantities DB1 can be expressed per — ``total`` is the euro itself. */
export type MarginBasis = 'total' | 'ha' | 'kg' | 'row_m' | 'plant';

/** Canonical order the switcher offers bases in — the drawer's order, € gesamt first. */
export const BASIS_ORDER: readonly MarginBasis[] = ['total', 'ha', 'kg', 'row_m', 'plant'];

interface BasisMeta {
  /** Switcher + chart-title label, e.g. "je Hektar". */
  label: string;
  /**
   * #2844 — the Je-Einheit panel heading, e.g. "Je Pflanze". Held verbatim rather than
   * capitalised from ``label`` at the call site: that surgery is not total over the four
   * bases ("je kg" would yield "Je kg", while this app has always headed that panel
   * "Je Kilogramm"), and derived copy is exactly what the verdict fields below avoid.
   */
  heading: string;
  /** Compact unit for a column header / €-suffix, e.g. "ha", "kg", "lfm", "Pflanze"; empty for the euro total (a bare "DB1"). */
  unit: string;
  /**
   * The QUANTITY's noun: the "keine {…} hinterlegt" omitted-rows caption, and
   * (#2844) the drawer's Je-Einheit figure label above the denominator itself.
   */
  missing: string;
  /**
   * Whether ranking this basis ACROSS crops is meaningful. ha/kg level across
   * crops; row_m/plant are within-crop only (#2303 comparability = "within_crop":
   * row geometry differs per crop, so €/lfm of Erdbeeren vs Spargel is nonsense).
   */
  crossCrop: boolean;
  /**
   * #2844 — the Je-Einheit panel's verdict, per basis, so heading / figures / unit
   * suffix / verdict all read from ONE source and can never name different things
   * (the lock-step rule #2695 E already imposes on heading-vs-Ertrag). Held as full
   * sentences rather than a composed subject because German gender makes
   * "trägt ihre/seine Kosten" underivable from the noun alone.
   */
  verdictOver: string;
  verdictUnder: string;
}

/** Static per-basis metadata — not per-tenant data, so it lives in the FE, not the fact. */
export const BASIS_META: Record<MarginBasis, BasisMeta> = {
  total: {
    label: '€ gesamt',
    heading: 'Gesamt',
    unit: '',
    // Never missing — the denominator is 1 — but the record is total over the union.
    missing: 'Deckungsbeitrag',
    crossCrop: true,
    verdictOver: 'Der Deckungsbeitrag liegt über Null.',
    verdictUnder: 'Der Deckungsbeitrag liegt unter Null.',
  },
  ha: {
    label: 'je Hektar',
    heading: 'Je Hektar',
    unit: 'ha',
    missing: 'Fläche',
    crossCrop: true,
    verdictOver: 'Jeder Hektar liegt über der Kostenlinie.',
    verdictUnder: 'Jeder Hektar liegt unter der Kostenlinie.',
  },
  kg: {
    label: 'je kg',
    heading: 'Je Kilogramm',
    unit: 'kg',
    missing: 'Erntemenge',
    crossCrop: true,
    // Byte-identical to the copy the drawer has shipped since #2168.
    verdictOver: 'Jedes Kilo liegt über der Kostenlinie.',
    verdictUnder: 'Jedes Kilo liegt unter der Kostenlinie.',
  },
  row_m: {
    label: 'je Reihenmeter',
    heading: 'Je Reihenmeter',
    unit: 'lfm',
    missing: 'Reihenmeter',
    crossCrop: false,
    verdictOver: 'Jeder Reihenmeter trägt seine Kosten.',
    verdictUnder: 'Jeder Reihenmeter liegt unter der Kostenlinie.',
  },
  plant: {
    label: 'je Pflanze',
    heading: 'Je Pflanze',
    unit: 'Pflanze',
    missing: 'Pflanzenzahl',
    crossCrop: false,
    verdictOver: 'Jede Pflanze trägt ihre Kosten.',
    verdictUnder: 'Jede Pflanze liegt unter der Kostenlinie.',
  },
};

/** The ``geometry_provenance`` keys #2303's materialiser stamps. */
type GeometryProvenanceKey = 'row_length_m' | 'plant_count';

/**
 * Which provenance entry backs each geometry basis — i.e. the bases whose denominator
 * #2303's materialiser DERIVES onto the fact. ``ha``/``kg`` come straight off the cycle,
 * so they have no assumption to disclose and get no second drawer panel: at ``basis=ha``
 * the drawer looks exactly as it did before #2844.
 *
 * An explicit map rather than a ``row_m``-or-else branch: a future geometry basis that
 * forgets to register here resolves to ``undefined`` — no sentence, no badge, no panel
 * provenance — instead of silently borrowing the plant count's.
 */
const GEOMETRY_PROVENANCE_KEY: Partial<Record<MarginBasis, GeometryProvenanceKey>> = {
  row_m: 'row_length_m',
  plant: 'plant_count',
};

export function isGeometryBasis(basis: MarginBasis): boolean {
  return GEOMETRY_PROVENANCE_KEY[basis] !== undefined;
}

/** The ``geometry_provenance`` entry behind ``basis`` — the one lookup the honesty
 *  sentence and its recorded/derived classification BOTH read, so they cannot diverge. */
const geometryEntry = (
  provenance: GeometryProvenance | null,
  basis: MarginBasis,
): GeometryProvenanceEntry | null => {
  const key = GEOMETRY_PROVENANCE_KEY[basis];
  if (!provenance || !key) return null;
  return provenance[key] ?? null;
};

/** ``recorded`` (directly on the crop_cycle) vs ``derived`` (a rung of #2303's ladder).
 *  Anything the worker did not stamp ``recorded`` counts as derived — understating trust
 *  is the safe degrade, since "recorded" is the strongest claim this line can make. */
const classifyEntry = (entry: GeometryProvenanceEntry): GeometrySource =>
  entry.source === 'recorded' ? 'recorded' : 'derived';

const round2 = (n: number): number => Math.round(n * 100) / 100;

const positiveOrNull = (v: number | null | undefined): number | null =>
  v != null && Number.isFinite(v) && v > 0 ? v : null;

/** de-DE decimal for the geometry honesty line (1.5 → "1,5"), finite only. */
const de = (n: number): string => n.toLocaleString('de-DE', { maximumFractionDigits: 3 });

export function isMarginBasis(v: unknown): v is MarginBasis {
  return v === 'total' || v === 'ha' || v === 'kg' || v === 'row_m' || v === 'plant';
}

/**
 * The harvest quantity in KILOGRAMS — the ``je kg`` denominator.
 *
 * #2930: the factor comes from the SHARED ``KG_PER_YIELD_UNIT`` table (dt ×100, t ×1000,
 * kg ×1), read case- and whitespace-insensitively, so every unit the drawer can NAME is a
 * unit this scaler can SCALE. Until #2930 this was one case-sensitive ``=== 'dt'``, which
 * ranked a ``yield_unit: 't'`` fact 1000x too high (``db1 / 306`` for 306 tonnes) under a
 * column headed "je kg" while the drawer headed the same number "Je Tonne". A unit this app
 * cannot name is still read as kg — the pre-existing degrade, unchanged, and identical to
 * the one ``yieldUnitLabel`` takes. Zero/absent → null (no basis, no divide-by-zero).
 */
export function yieldInKg(row: Pick<DeckungsbeitragRow, 'yield' | 'yield_unit'>): number | null {
  return positiveOrNull(row.yield * kgPerYieldUnit(row.yield_unit));
}

/** The denominator quantity for ``basis`` on one row — null when unavailable/zero. */
export function basisQuantity(row: DeckungsbeitragRow, basis: MarginBasis): number | null {
  switch (basis) {
    case 'total':
      return 1;
    case 'ha':
      return positiveOrNull(row.area_ha);
    case 'kg':
      return yieldInKg(row);
    case 'row_m':
      return positiveOrNull(row.row_length_m);
    case 'plant':
      return positiveOrNull(row.plant_count);
  }
}

/**
 * DB1 per one unit of ``basis`` on one row — null when the denominator is
 * unavailable. For ``ha`` this returns the fact-stored ``db1_per_ha`` verbatim
 * (byte-identical to today's ranking), NOT a re-division.
 */
export function db1PerBasis(row: DeckungsbeitragRow, basis: MarginBasis): number | null {
  if (basis === 'total') return row.db1;
  if (basis === 'ha') return row.db1_per_ha;
  const q = basisQuantity(row, basis);
  return q === null ? null : round2(row.db1 / q);
}

/**
 * Quantity-weighted aggregate Σdb1/Σquantity — the ONLY correct group per-X (a
 * mean of member per-X values misstates unequal members, the issue's called-out
 * trap). Null unless EVERY member carries the quantity (mirrors rollupByKultur's
 * full-coverage rule), and null on an empty set or a zero denominator. Rounds the
 * sums before dividing, exactly like the rollup, so ``ha`` stays consistent.
 */
export function aggregateDb1PerBasis(rows: DeckungsbeitragRow[], basis: MarginBasis): number | null {
  if (rows.length === 0) return null;
  // € gesamt is the SUM — Σdb1/Σ1 would be the mean, exactly the trap this function exists to avoid.
  if (basis === 'total') return round2(rows.reduce((acc, row) => acc + row.db1, 0));
  let sumDb1 = 0;
  let sumQ = 0;
  for (const row of rows) {
    const q = basisQuantity(row, basis);
    if (q === null) return null;
    sumDb1 += row.db1;
    sumQ += q;
  }
  const denom = round2(sumQ);
  return denom > 0 ? round2(round2(sumDb1) / denom) : null;
}

/**
 * Group per-X for a Kultur rollup: the stored area-weighted ``db1_per_ha`` for
 * ``ha`` (unchanged, byte-identical), else Σdb1/Σquantity over the members.
 *
 * HOF-195 widened the parameter from ``KulturRollupRow`` to exactly the two fields the
 * body reads, so the Hauptschlag analysis unit — which carries the same area-weighted
 * ``db1_per_ha`` over the same shape of member list — answers every Bezugsgröße through
 * THIS path rather than a second implementation. One aggregate, three grains.
 */
export function db1PerBasisForGroup(
  group: Pick<KulturRollupRow, 'db1_per_ha' | 'members'>,
  basis: MarginBasis,
): number | null {
  return basis === 'ha' ? group.db1_per_ha : aggregateDb1PerBasis(group.members, basis);
}

/**
 * The bases to OFFER, in canonical order — the drawer's set: ``total`` and ``ha`` always,
 * every other basis when some row carries its quantity. Until 2026-09-07 the within-crop
 * bases (row_m / plant) were also withheld while more than one crop was in view (``basisScope``,
 * #2303's comparability note); the farmer wanted them on the mixed page, so the gate went.
 * What stays honest is the per-row null: a Kultur without the quantity ranks last with a
 * dash, never a fabricated 0 €/X. ``crossCrop`` remains on the meta for a future hint.
 */
export function availableBases(rows: DeckungsbeitragRow[]): MarginBasis[] {
  return BASIS_ORDER.filter(
    (basis) => basis === 'total' || basis === 'ha' || rows.some((row) => basisQuantity(row, basis) !== null),
  );
}

/**
 * Resolve the active basis: URL param → ``'kg'`` → tenant default → ``'ha'``, each
 * accepted only when it is in ``offered`` (so a ``?basis=plant`` deep link degrades at
 * all-crops scope, and a stale tenant default never sticks a basis the data no longer
 * supports). ``'ha'`` is always offered → the fallback is total.
 *
 * #2948 §7 put ``'kg'`` ahead of the tenant default: the page LANDS on *je kg*, the
 * number a farmer prices in, rather than on *je Hektar*. Because ``basis`` is
 * deliberately SHARED between chart and table (#2304), this is one default for both —
 * there is no chart-only landing basis to change.
 *
 * Degradation needs no second branch: ``availableBases`` withholds ``'kg'`` entirely
 * for a tenant where no row carries an Erntemenge, so ``can('kg')`` is false there and
 * the resolution falls through to the tenant default and then ``'ha'`` exactly as
 * before. The one deliberate cost: ``'kg'`` now beats an EXPLICITLY configured tenant
 * default (vacuous today — ``deckungsbeitrag_default_basis`` is unset for every tenant
 * — but it inverts the #2319 operator-control semantics for a future ``row_m`` tenant).
 */
export function resolveBasis(
  param: string | null,
  tenantDefault: MarginBasis,
  offered: MarginBasis[],
): MarginBasis {
  const can = (b: MarginBasis): boolean => offered.includes(b);
  if (param && isMarginBasis(param) && can(param)) return param;
  if (can('kg')) return 'kg';
  if (can(tenantDefault)) return tenantDefault;
  return 'ha';
}

/**
 * The honest one-liner behind a derived Bezugsgröße denominator — the assumption
 * (row spacing / plant density, recorded vs derived) the je-Reihenmeter /
 * je-Pflanze figure rests on, for the detail panel. Null when ``basis`` is not a
 * geometry basis or the fact carries no matching provenance.
 */
export function geometryAssumptionText(
  provenance: GeometryProvenance | null,
  basis: MarginBasis,
): string | null {
  const key = GEOMETRY_PROVENANCE_KEY[basis];
  const entry = geometryEntry(provenance, basis);
  if (!entry || !key) return null;
  const noun = BASIS_META[basis].missing;
  // Switched on the KEY actually fetched, exhaustively: a new provenance key added to
  // GeometryProvenanceKey fails the typecheck here rather than silently inheriting the
  // plant-count sentences.
  switch (key) {
    case 'row_length_m':
      if (entry.source === 'derived_from_row_spacing' && entry.row_spacing_m != null) {
        return `${noun} aus Reihenabstand ${de(entry.row_spacing_m)} m abgeleitet.`;
      }
      // HOF-312 — the SCHLAG's pitch, named as such. The two sentences differ by where the
      // constant was recorded, and that difference is the whole point: the field value is a
      // farm convention that holds for every planting on the tunnel, not a row geometry
      // measured for this crop. Collapsing them would make a number nobody can place.
      if (entry.source === 'derived_from_field_row_spacing' && entry.row_spacing_m != null) {
        return `${noun} aus dem am Schlag erfassten Reihenabstand ${de(entry.row_spacing_m)} m abgeleitet.`;
      }
      if (entry.source === 'derived_from_tunnels' && entry.tunnels != null) {
        return `${noun} aus ${entry.tunnels} Tunneln des Schlags abgeleitet (Reihen × Länge${
          entry.field_row_length_m != null ? `, ${de(entry.field_row_length_m)} lfm auf dem Schlag` : ''
        }).`;
      }
      break;
    case 'plant_count':
      if (entry.source === 'derived_from_plants_per_m' && entry.plants_per_m != null) {
        return `${noun} aus ${de(entry.plants_per_m)} Pflanzen/m abgeleitet.`;
      }
      if (entry.source === 'derived_from_plants_per_m2' && entry.plants_per_m2 != null) {
        return `${noun} aus ${de(entry.plants_per_m2)} Pflanzen/m² abgeleitet.`;
      }
      break;
  }
  // The rung is stamped but its density value is absent (an older fact, or a defensive
  // mapper that dropped a non-finite number). The sentence degrades — but it may NOT
  // fall through to "direkt erfasst", which would upgrade a derived figure to a
  // recorded one, the strongest trust claim this line can make.
  return classifyEntry(entry) === 'recorded' ? `${noun} direkt erfasst.` : `${noun} abgeleitet.`;
}

/** ``recorded`` (directly on the crop_cycle) vs ``derived`` (a rung of #2303's ladder). */
export type GeometrySource = 'recorded' | 'derived';

/**
 * #2844 — how the ``basis`` denominator was obtained, for the panel that styles a
 * DERIVED figure differently from a recorded one. Read off the SAME entry
 * ``geometryAssumptionText`` reads, through the same classifier, so badge and sentence
 * can never contradict each other; null exactly where that sentence is null.
 */
export function geometryAssumptionSource(
  provenance: GeometryProvenance | null,
  basis: MarginBasis,
): GeometrySource | null {
  const entry = geometryEntry(provenance, basis);
  return entry ? classifyEntry(entry) : null;
}
